import React, { useState } from 'react';
import { Sidebar } from '../../components/shared/Sidebar';
import { DashboardHeader } from '../../components/shared/DashboardHeader';
import { MOCK_PAYMENTS } from '../../utils/mockData';
import { formatCurrency } from '../../utils/calculations';
import { motion } from 'framer-motion';
import { Download, Calendar, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { PaymentConfirmation } from '../../components/modals/PaymentConfirmation';
export function PaymentSchedule() {
  const {
    toast
  } = useToast();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownload = () => {
    setIsDownloading(true);
    setTimeout(() => {
      setIsDownloading(false);
      toast.success('Statement downloaded successfully');
    }, 1500);
  };
  const handlePaymentSuccess = () => {
    toast.success('Payment processed successfully!');
    setShowPaymentModal(false);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 whitespace-nowrap" data-id="element-4809">
            <CheckCircle className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-4810" /> Paid
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 whitespace-nowrap" data-id="element-4811">
            <Clock className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-4812" /> Pending
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 whitespace-nowrap" data-id="element-4813">
            <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-4814" /> Overdue
          </span>;
    }
  };
  return <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors pb-16 lg:pb-0" data-id="element-4815">
      <Sidebar data-id="element-4816" />

      <main className="flex-1 lg:ml-[240px] transition-all duration-300" data-id="element-4817">
        <DashboardHeader data-id="element-4818" />

        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-id="element-4819">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-4820">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 lg:mb-8 gap-4" data-id="element-4821">
              <div className="min-w-0" data-id="element-4822">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate" data-id="element-4823">
                  Payment Schedule
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2" data-id="element-4824">
                  Track your monthly rent payments and history.
                </p>
              </div>
              <button onClick={handleDownload} disabled={isDownloading} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors whitespace-nowrap flex-shrink-0" data-id="element-4825">
                <Download className={`h-4 w-4 mr-2 flex-shrink-0 ${isDownloading ? 'animate-bounce' : ''}`} data-id="element-4826" />
                {isDownloading ? 'Downloading...' : 'Download Statement'}
              </button>
            </div>

            {/* Upcoming Payment Card */}
            <div className="bg-gradient-to-r from-blue-700 to-blue-900 dark:from-blue-800 dark:to-blue-950 rounded-2xl p-6 sm:p-8 text-white shadow-lg mb-6 lg:mb-8 relative overflow-hidden transition-colors" data-id="element-4827">
              <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-white opacity-10 rounded-full blur-2xl" data-id="element-4828"></div>

              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center relative z-10 gap-4" data-id="element-4829">
                <div className="min-w-0 flex-1" data-id="element-4830">
                  <p className="text-blue-200 text-sm font-medium mb-1 uppercase tracking-wider truncate" data-id="element-4831">
                    Next Payment Due
                  </p>
                  <h2 className="text-4xl font-bold mb-2 break-all" data-id="element-4832">
                    {formatCurrency(150000)}
                  </h2>
                  <div className="flex items-center text-blue-100 text-sm bg-blue-800/50 dark:bg-blue-900/50 px-3 py-1 rounded-full w-fit" data-id="element-4833">
                    <Calendar className="h-4 w-4 mr-2 flex-shrink-0" data-id="element-4834" />
                    <span className="truncate" data-id="element-4835">Due on November 1, 2023</span>
                  </div>
                </div>
                <button onClick={() => setShowPaymentModal(true)} className="bg-white text-blue-700 px-8 py-3 rounded-lg font-bold hover:bg-blue-50 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 whitespace-nowrap flex-shrink-0" data-id="element-4836">
                  Pay Now
                </button>
              </div>
            </div>

            {/* Payment History */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors" data-id="element-4837">
              <div className="px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-700/50" data-id="element-4838">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate" data-id="element-4839">
                  Payment History
                </h3>
              </div>
              <div className="overflow-x-auto" data-id="element-4840">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4841">
                  <thead className="bg-gray-50 dark:bg-gray-700/50" data-id="element-4842">
                    <tr data-id="element-4843">
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap" data-id="element-4844">
                        Date
                      </th>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap" data-id="element-4845">
                        Description
                      </th>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap" data-id="element-4846">
                        Amount
                      </th>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap" data-id="element-4847">
                        Status
                      </th>
                      <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap" data-id="element-4848">
                        Receipt
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4849">
                    {MOCK_PAYMENTS.map((payment, index) => <motion.tr key={payment.id} initial={{
                    opacity: 0,
                    x: -10
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: index * 0.05
                  }} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors" data-id="element-4850">
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4851">
                          {new Date(payment.date).toLocaleDateString()}
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white capitalize" data-id="element-4852">
                          {payment.type} Payment
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-medium" data-id="element-4853">
                          {formatCurrency(payment.amount)}
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap" data-id="element-4854">
                          {getStatusBadge(payment.status)}
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-4855">
                          {payment.status === 'paid' && <button onClick={() => toast.success('Receipt downloaded')} className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:underline" data-id="element-4856">
                              Download
                            </button>}
                        </td>
                      </motion.tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      <PaymentConfirmation isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} onConfirm={handlePaymentSuccess} amount={150000} date="November 1, 2023" property="Sunrise Apartments, Flat 4B" data-id="element-4857" />
    </div>;
}